// composition root — 포트에 구현을 꽂고, 플러그인을 부팅하고, 라우트를 조립한다
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider, type RouteObject } from 'react-router';

import { createHttpHealthSource } from '../adapters/http-health-source';
import { AppLayout } from '../presentation/app-layout';
import { HomePage } from '../presentation/home-page';
import { NotFoundPage } from '../presentation/not-found-page';
import { PluginErrorPage } from '../presentation/plugin-error-page';
import { appPluginConfig } from './plugin-config';
import { PluginHostProvider } from './plugin-host-context';
import { PluginErrorBoundary } from './plugin-runtime/boundary';
import { bootPlugins, type PluginErrorReporter } from './plugin-runtime/host';
import { plugins } from './plugins';
import { PortsProvider, type Ports } from './ports-context';

// 플러그인 오류는 화면을 멈추지 않는다. 개발 중에는 콘솔에 남기고, 배포에서 어디로 보낼지(로그 수집)는 MVP 가 정한다
const onPluginError: PluginErrorReporter = (plugin, phase, error) => {
  if (import.meta.env.DEV) console.warn(`[plugin:${plugin}] ${phase} 실패`, error);
};

const queryClient = new QueryClient();
const ports: Ports = { health: createHttpHealthSource() };
// 원격 설정 층은 RemotePluginConfigSource 를 연결할 때 layers 뒤에 붙인다 (adapters/no-remote-plugin-config.ts)
const host = bootPlugins(plugins, { layers: [appPluginConfig], onError: onPluginError });

const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <HomePage /> },
      ...host.routes.map(({ plugin, path, component: Page }) => ({
        path: path.replace(/^\//, ''),
        element: (
          <PluginErrorBoundary plugin={plugin} onError={host.reportError} fallback={<PluginErrorPage />}>
            <Page />
          </PluginErrorBoundary>
        ),
      })),
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
const router = createBrowserRouter(routes);

export function Root() {
  return (
    <QueryClientProvider client={queryClient}>
      <PortsProvider ports={ports}>
        <PluginHostProvider host={host} locale="ko">
          <RouterProvider router={router} />
        </PluginHostProvider>
      </PortsProvider>
    </QueryClientProvider>
  );
}
